import React from "react";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import Text from "@/components/ui/text";
import { ArrowRight } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import { formatDateShort } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { TermsFieldChange } from "./types";
import { discountUnitFor, displayValue } from "./changeDisplayValue";

interface Props {
  changes: TermsFieldChange[];
  effectiveDate?: string;
  // Money values in the tooltip are denominated in the contract's currency.
  currency: string | null;
  // The row's current discount unit, used for whichever side of a discount
  // change did not itself change the unit.
  isDiscountPercentage: boolean;
}

// Marks a value the pending update changed, with its before and after on hover.
const TermsChangeDot: React.FC<Props> = ({
  changes,
  effectiveDate,
  currency,
  isDiscountPercentage
}) => {
  const { t } = useTranslation();
  if (changes.length === 0) return null;

  const contextFor = (side: "before" | "after") => ({
    t,
    currency,
    isDiscountPercentage: discountUnitFor(changes, side, isDiscountPercentage)
  });

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* A button, not a span: the before-and-after values are only in the tooltip.
            The padding is the hit area — an 8px dot alone is too small to aim at. */}
        <button
          type="button"
          aria-label={t("contracts.updateTerms.change.indicatorLabel")}
          className="flex shrink-0 items-center justify-center rounded-full p-1 transition-colors duration-200 ease-in-out hover:bg-surface-hover-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
          data-test="terms-change-dot"
        >
          <span className="size-2 rounded-full bg-blue-400" />
        </button>
      </TooltipTrigger>
      <TooltipContent enableMarkdown={false} maxWidth={24} className="p-3">
        <div className="flex flex-col gap-3">
          {changes.map((change) => (
            <div key={change.field} className="flex flex-col gap-2">
              <Text variant="smBodySecondary">{change.label}</Text>
              <div className="flex items-start gap-3">
                <div className="flex flex-col gap-1 min-w-0">
                  <Text variant="smBodySecondary">
                    {t("contracts.updateTerms.change.before")}
                  </Text>
                  <Text variant="body">
                    {displayValue(
                      change.before,
                      change.field,
                      contextFor("before")
                    )}
                  </Text>
                </div>
                <div className="pt-5 shrink-0">
                  <IconContainer size="5" color="secondary" nativeStroke>
                    <ArrowRight />
                  </IconContainer>
                </div>
                <div className="flex flex-col gap-1 min-w-0">
                  <Text variant="smBodySecondary">
                    {effectiveDate
                      ? t("contracts.updateTerms.change.asOfDate", {
                          date: formatDateShort(effectiveDate)
                        })
                      : t("contracts.updateTerms.change.after")}
                  </Text>
                  <Text variant="body">
                    {displayValue(
                      change.after,
                      change.field,
                      contextFor("after")
                    )}
                  </Text>
                </div>
              </div>
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export default TermsChangeDot;
